import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ClusterListItem } from '@b4b/shared';
import { useReadAll, useSetRead, useSetSaved } from '../api/queries.js';
import { useKeyboardShortcuts } from './useKeyboardShortcuts.js';

/** Keyboard navigation for card layouts (list/grid/topics): j/k move a focus
 * ring over the cards, the other shortcuts act on the focused card. */
export function useCardKeyboard(items: ClusterListItem[]): number {
  const navigate = useNavigate();
  const setRead = useSetRead();
  const setSaved = useSetSaved();
  const readAll = useReadAll();
  const [focusIndex, setFocusIndex] = useState(-1);

  // keep the index valid when the list shrinks (items get read/hidden)
  useEffect(() => {
    if (focusIndex >= items.length) setFocusIndex(items.length - 1);
  }, [items.length, focusIndex]);

  const focused = () => items[focusIndex];
  const move = (delta: number) => {
    const next = Math.max(0, Math.min(items.length - 1, focusIndex + delta));
    setFocusIndex(next);
    const item = items[next];
    if (item) {
      document
        .querySelector(`[data-cluster-id="${item.id}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  useKeyboardShortcuts({
    next: () => move(1),
    prev: () => move(-1),
    open: () => {
      const item = focused();
      if (item) navigate(`/c/${item.id}`);
    },
    openOriginal: () => {
      const item = focused();
      if (item) {
        window.open(item.representative.url, '_blank', 'noopener');
        if (!item.read) setRead.mutate({ id: item.id, read: true });
      }
    },
    toggleSaved: () => {
      const item = focused();
      if (item) setSaved.mutate({ id: item.id, saved: !item.saved });
    },
    toggleRead: () => {
      const item = focused();
      if (item) setRead.mutate({ id: item.id, read: !item.read });
    },
    markAllRead: () => readAll.mutate(new Date().toISOString()),
    search: () => navigate('/search'),
  });

  return focusIndex;
}
